// raw-fetch-ok-file: v0y4ge/voyage-fleet-management#676 -- pre-existing raw-fetch shadow API layer (studio-shell/coord-shell internal *-api.ts or identity/integrations-worker call), predates @v0y4ge/api-client convergence; tracked backlog, not a new violation.
// Studio frontend entry wrapper. The ENTIRE app UI is Claude Design's, mounted
// verbatim from ./cd-app (imported via tools/cd-import). This file is ONLY the
// tenant-native auth gate: it resolves the session (useWhoami against the
// identity worker), redirects unauthenticated users to the tenant login, and —
// once authenticated — renders CD's <App/> unchanged. No StudioShell wiring
// lives here anymore; CD owns the shell + every screen.

import { useCallback, useEffect, useRef, useState } from "react";
import { SignInScreen, type TenantBranding } from "@v0y4ge/studio-shell";
import { IDENTITY_BASE_URL, IDENTITY_TENANT_SLUG, tenantLoginUrl } from "./lib/signin-url";
import { useWhoami } from "./lib/useWhoami";
import { useDemoMode } from "./lib/useDemoMode";
import { useTenantTimeZone } from "./lib/tenantDateTime";
import { setAuthenticated, useAuth, ROLE_CATALOG, getCustomRoles, type CurrentUser } from "./cd-app/shell/access";
import { logout } from "@v0y4ge/api-client";
import { projectStudioUiCapabilities } from "@v0y4ge/api-contracts";
import CDApp from "./cd-app/App";
import { shouldMountStudioTour, isAttendHost } from "./tour-routes";

// Sign-out revoke budget. Same 8s as useWhoami's probe -- past this the user is
// better served by the sign-in page than by an indefinite spinner.
const SIGN_OUT_TIMEOUT_MS = 8000;
const WORKSPACE_DISPLAY_NAME = ((import.meta.env.VITE_WORKSPACE_DISPLAY_NAME as string | undefined) ?? "").trim();
const WORKSPACE_PRODUCT_NAME = ((import.meta.env.VITE_WORKSPACE_PRODUCT_NAME as string | undefined) ?? "").trim();
// Compose-emitted per-tenant demo_mode HINT (#655, demoted by #686) — baked at
// build time by skills/compose-tenant-deployment from tenant-manifest
// `tenant.demoMode`. This is NO LONGER the gate itself: it is only a
// first-paint seed for `useDemoMode`'s initial state (before its runtime
// probe resolves). The actual gate is `demo.enabled` below, which reads live
// D1 `tenants.demo_mode` at runtime via the SAME `/demo/personas` op CD's own
// persona chooser calls — see `./lib/useDemoMode.ts`. This makes the fleet
// admin toggle instantly authoritative with NO recompose/redeploy: a stale
// baked `true` can no longer keep the chooser showing after an operator
// flips demo_mode off (or vice versa), because the render/redirect decisions
// below always wait for the probe to settle rather than trusting this hint.
const DEMO_MODE_HINT = ((import.meta.env.VITE_DEMO_MODE as string | undefined) ?? "").trim().toLowerCase() === "true";

function buildLocalSignInBranding(): TenantBranding {
  return {
    displayName: WORKSPACE_DISPLAY_NAME || "Your Workspace",
    productName: WORKSPACE_PRODUCT_NAME || "Studio",
    heroTitle: "Plan, run, and learn from every event you put on.",
    heroBody: "One workspace for the calendar, guest list, communications, and numbers that prove it worked.",
    footerText: "Powered by JRNI",
  };
}

// Best-effort bridge from the identity worker membership-role taxonomy to
// cd-app's Studio permission-role taxonomy. Product/CD should reconcile these
// role models properly later.
//
// cd-drop3-ingest (studio-master@4fdb673, packet-3): this drop renamed CD's
// entire process-scoped role catalog (shell/access.ts ROLE_CATALOG) from the
// old "Admin"/"Editor"/"Approver"/"Viewer" vocabulary to five named Events
// roles: "Event Creator", "Event Coordinator", "Event Manager",
// "Event Approver", "System Administrator". Unmapped role strings resolve to
// zero effective capabilities (effectivePerms only awards perms for
// assignments.events values matching a real ROLE_CATALOG id) — every mapping
// below MUST target one of those five exact strings or the mapped user gets
// silently locked out of every capability.
//   owner / admin / platform_admin -> "System Administrator" (full access,
//     was "Admin").
//   member -> "Event Creator" (authors/edits content, was "Editor").
//   finance / support / reporting -> "Event Approver" (reviews/signs off,
//     was "Approver" — same semantic target, renamed).
// KNOWN GAP (pushback tracked alongside packet-3's tsc discrepancy): the new
// catalog has NO read-only-viewer-equivalent role — the closest non-admin
// role (Event Approver) still carries write-ish capabilities
// (events.unpublish, campaigns.approve), so granting it to a "viewer"
// membership would OVER-privilege them. Safer to fail closed: "viewer" (and
// any unrecognized role) maps to a label with NO ROLE_CATALOG entry, which
// effectivePerms resolves to zero capabilities rather than guessing.
// #702: demo-persona sessions (fresh sign-in AND reload alike) carry an
// already-canonical ROLE_CATALOG label in `session.role` (useWhoami's
// sessionFromWhoami prefers whoami's `user.role` -- the persona's real CD
// label, e.g. "Event Coordinator" -- over the collapsed MembershipRole slug).
// Passing one of those five exact labels through the slug switch below would
// mismatch every case (none of them lowercase to a known slug) and silently
// fall to "Viewer", re-collapsing the persona on every reload. So a role that
// is ALREADY one of the five canonical labels wins outright, unchanged, and
// only a non-canonical value (a real MembershipRole slug, for every
// non-demo/legacy session) goes through the slug mapping below.
// Session-refresh role-collapse fix (2026-07-28): this used to be a
// hand-listed set of ONLY the five Events labels, so a reload for any
// persona in another process (Queue Manager, Operations Admin, Personal
// Banker, ...) fell through to the slug switch below, matched nothing, and
// silently collapsed to "Viewer" -- zero capabilities, NoAccess screen.
// Derived from the live `ROLE_CATALOG` (every process, not just events) so a
// role that already IS a real catalog label always survives verbatim on
// reload, and a newly added catalog role never needs a matching edit here.
const STUDIO_ROLE_LABELS: ReadonlySet<string> = new Set(
  Object.values(ROLE_CATALOG).flatMap((defs) => defs.map((def) => def.id)),
);

export function mapMembershipRoleToStudioRole(role: string | null | undefined): CurrentUser["role"] {
  const trimmed = (role ?? "").trim();
  if (STUDIO_ROLE_LABELS.has(trimmed)) return trimmed;
  switch (trimmed.toLowerCase()) {
    case "owner":
    case "admin":
    case "platform_admin":
      return "System Administrator";
    case "member":
      return "Event Creator";
    case "finance":
    case "support":
    case "reporting":
      return "Event Approver";
    case "viewer":
    default:
      return "Viewer";
  }
}

const SESSIONLESS_STUDIO_ROUTES: ReadonlySet<string> = new Set([
  "/queue/display",
  "/queue/join",
]);

// A hit is a public attendee route (no tenant SSO redirect; falls straight
// through to <CDApp/>) when the pathname is an explicitly session-less Studio
// route, a `/public/*` Studio route, OR the request is on the
// `attend.<tenant>` host — where CD's anonymous attendee SPA is served at the
// bare `/<slug>` path (Option 2). The host arm is guarded to the literal
// `attend` first DNS label, so this stays a NO-OP on `studio.<tenant>` and
// every non-attend host.
export function isPublicAttendeeRoute(pathname: string, hostname: string = window.location.hostname): boolean {
  return SESSIONLESS_STUDIO_ROUTES.has(pathname)
    || isAttendHost(hostname)
    || !shouldMountStudioTour(pathname);
}

function displayNameFromSession(displayName: string | undefined, email: string): string {
  const trimmed = (displayName ?? "").trim();
  return trimmed || email.split("@")[0] || email;
}

function initialsFromName(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const parts = trimmed.includes("@") ? [trimmed.split("@")[0] ?? ""] : trimmed.split(/\s+/);
  return parts.map((part) => part[0]).join("").slice(0, 2).toUpperCase();
}

export function App() {
  const renderPublicAttendeeRoute = isPublicAttendeeRoute(window.location.pathname);
  // Public attend pages are anonymous by contract. Do not probe the identity
  // worker on them: a deliberate 401 is still a failed XHR to browsers and
  // rehearsal tooling, and the result cannot affect this render path.
  const whoami = useWhoami(IDENTITY_BASE_URL, IDENTITY_TENANT_SLUG, !renderPublicAttendeeRoute);
  // Runtime probe (#686): resolves the SAME live D1 `tenants.demo_mode` the
  // `/demo/personas` and `/demo/sign-in` backend routes already gate on, by
  // actually calling `getDemoPersonas()` — see `./lib/useDemoMode.ts` for the
  // full rationale (reuse over new surface, fail-safe-OFF on any non-200).
  const demo = useDemoMode(IDENTITY_BASE_URL, IDENTITY_TENANT_SLUG, DEMO_MODE_HINT);
  const loginUrl = tenantLoginUrl();
  const renderLocalSignIn = !IDENTITY_BASE_URL && window.location.pathname === "/sign-in";
  const session = whoami.session;

  // Demo persona-chooser gate (#655, made runtime-authoritative by #686): a
  // demo_mode=on tenant with a non-empty catalog mounts CDApp (below)
  // instead of redirecting to real SSO, once BOTH whoami and the demo probe have
  // resolved and whoami says "not authenticated". CDApp's own AuthGate
  // (shell/access.ts's `useAuth().signedIn`) then renders CD's OWN
  // <Signin/> persona chooser — it fetches personas and signs in via the
  // wired `getDemoPersonas`/`demoSignIn` ops on `@v0y4ge/api-client`
  // (see shell/access.ts's `fetchPersonas`/`signIn`), no host-side
  // duplication needed. Everything else (loading chrome, public attendee
  // routes, the authenticated CDApp render) is completely unchanged; this
  // gate controls only WHETHER an unauthenticated hit is allowed to reach
  // CDApp instead of being redirected — never WHICH sign-in component
  // renders once there.
  const demoSignInAvailable = demo.enabled && !demo.empty;
  const renderDemoSignIn =
    demoSignInAvailable &&
    !renderLocalSignIn &&
    !renderPublicAttendeeRoute &&
    !whoami.loading &&
    !demo.loading &&
    !whoami.authenticated;

  // Anchor the datetime runtime to the TENANT's configured zone before any
  // screen paints a timestamp. Gated on the session being resolved (real SSO
  // or demo-mode) because the public-config read is a tenant-scoped API call;
  // it is idempotent and self-guards against duplicate fetches.
  useTenantTimeZone(
    !renderPublicAttendeeRoute
    && !whoami.loading
    && !demo.loading
    && (whoami.authenticated || demoSignInAvailable),
  );

  useEffect(() => {
    if (
      whoami.loading ||
      demo.loading ||
      whoami.authenticated ||
      renderLocalSignIn ||
      renderPublicAttendeeRoute ||
      demoSignInAvailable
    ) {
      return;
    }
    window.location.assign(loginUrl);
  }, [
    loginUrl,
    renderLocalSignIn,
    renderPublicAttendeeRoute,
    whoami.authenticated,
    whoami.loading,
    demo.loading,
    demoSignInAvailable,
  ]);

  // ─── JAI-920: sign-out is a DATA-LAYER operation, and it happens here ─────
  //
  // CD's `signOut()` (cd-app/shell/access.ts) clears three module-level
  // variables, calls notify() and — since the 2026-09-07 pack — awaits the
  // identity host's /auth/logout itself. That call is idempotent, so this
  // wrapper still owns the platform half: revoking a session, clearing its cookies and
  // emitting the audit event are data-layer concerns, which are ours. So CD
  // owns the affordance (the "Sign out" button) and this wrapper owns the
  // effect -- exactly the split the rest of this file already applies to
  // sign-IN via `setAuthenticated`.
  //
  // Before this, NOTHING owned the effect. `POST /auth/logout` was never
  // called from Studio at all (the only `logout` references in the app were
  // `Icon.logout`), so after "Sign out" the session row kept
  // `revoked_at IS NULL`, the cookie stayed live and valid, and no
  // `auth.session_revoked` event was ever written -- sign-ins with no matching
  // sign-outs in the audit trail. The visible symptom (JAI-920: an empty role
  // picker) was only the last link: `whoami.authenticated` stayed stale-true,
  // which disarmed both the redirect above and `renderDemoSignIn`, leaving
  // CDApp's AuthGate to render CD's demo <Signin/> on a tenant that has no
  // personas to offer.
  //
  // `whoami.refresh()` is what re-arms those two gates off a real answer. Once
  // it resolves unauthenticated, the EXISTING logic routes both cases
  // correctly with no new branch: a non-demo tenant redirects to the hosted
  // login (the effect above), and a demo tenant renders the persona chooser
  // (`renderDemoSignIn`). This effect deliberately adds no routing of its own.
  const cdSignedIn = useAuth().signedIn;
  const hadSession = useRef(false);
  const [signingOut, setSigningOut] = useState(false);
  const refreshWhoami = whoami.refresh;

  const revokeSessionAndReresolve = useCallback(async () => {
    setSigningOut(true);
    try {
      // Best-effort, exactly as studio-shell's AvatarDropdown treats it: if the
      // revoke cannot be delivered we still must not strand the user on a
      // signed-out-looking app.
      //
      // Deliberately NOT claimed here: that a survived session recovers cleanly.
      // CD's SIGNED_IN is already false by this point. The setAuthenticated
      // effect below DOES re-fire (whoami.loading is in its deps and necessarily
      // toggles across a refresh) -- the gap is its own early return, which skips
      // hydration when neither a resolvable role nor a resolvable assignment is
      // present. So a failed revoke can still land on CD's persona chooser.
      // The real mitigation is that the revoke should not fail -- the route now
      // answers the CORS preflight, which is what made this path reachable at
      // all. If it does fail, the console warning is the only signal; surfacing
      // it to the user is tracked separately.
      // Bounded, mirroring useWhoami's WHOAMI_TIMEOUT_MS. api-client applies no
      // default timeout, and `setSigningOut(false)` lives in the finally -- so a
      // socket that STALLS rather than fails would pin the loading chrome below
      // forever, with no way out. An error already recovers via the catch; this
      // covers the case that never settles.
      const abort = new AbortController();
      const timer = window.setTimeout(() => abort.abort(), SIGN_OUT_TIMEOUT_MS);
      try {
        await logout({ baseUrl: IDENTITY_BASE_URL, surface: "studio", signal: abort.signal });
      } finally {
        window.clearTimeout(timer);
      }
    } catch (error) {
      console.warn("[studio] sign-out could not revoke the session:", error);
    } finally {
      setSigningOut(false);
      refreshWhoami();
    }
  }, [refreshWhoami]);

  useEffect(() => {
    // Fire ONLY on a true signed-in -> signed-out transition. CD's SIGNED_IN
    // defaults to false, so without this latch every cold boot would post a
    // logout for a session that was never established.
    if (cdSignedIn) {
      hadSession.current = true;
      return;
    }
    if (!hadSession.current) return;
    hadSession.current = false;
    void revokeSessionAndReresolve();
  }, [cdSignedIn, revokeSessionAndReresolve]);

  // Packet-3 sign-in composition (v0y4ge/voyage-fleet-management, cd-drop3-ingest):
  // CD's own App.tsx now wraps AppRouter in an AuthGate (`useAuth().signedIn`,
  // shell/access.ts) that shows CD's OWN <Signin/> persona chooser whenever
  // access.ts's internal SIGNED_IN flag is false. Previously this effect called
  // the plain `setUser()` (hydrate CURRENT_USER only); left unchanged, a
  // real-SSO-authenticated tenant user would reach this branch, CDApp would
  // mount, and CD's AuthGate would immediately show ITS OWN Signin again
  // (SIGNED_IN defaults false) — a redundant second sign-in prompt on top of
  // the tenant-native login this file already resolved above. `setAuthenticated`
  // is the new exported setter CD added for exactly this host-driven bypass
  // (see access.ts's own doc comment on the function): it hydrates CURRENT_USER
  // AND flips SIGNED_IN, so CD's AuthGate renders AppRouter directly and its
  // own Signin never appears for a session this wrapper already resolved.
  // This is the ONLY change to this effect. `renderDemoSignIn` (below) is
  // unaffected: it keeps deciding whether the FIRST unauthenticated hit for
  // a demo_mode tenant is allowed to reach CDApp at all (vs. redirected to
  // the tenant's real SSO login); once there, CD's own Signin is what
  // renders for that first hit, and if that session round-trips through a
  // real reload/whoami resolution afterward, THIS effect is what reconciles
  // it into the host's role-mapped CurrentUser via `setAuthenticated`.
  useEffect(() => {
    if (whoami.loading || !whoami.authenticated) return;
    const email = session.user.email.trim();
    if (!email) return;
    const role = mapMembershipRoleToStudioRole(session.role);
    // Session-refresh role-collapse fix (2026-07-28): assignments carry
    // through UNCHANGED from useWhoami (already normalized onto studio-role
    // labels there) -- this is the per-process bundle
    // `setAuthenticated`/`effectivePerms` (cd-app/shell/access.ts) actually
    // resolve capabilities from; dropping it (the pre-fix behavior) is what
    // collapsed every non-Events persona to zero capabilities on reload.
    const assignments = session.assignments;
    // Second defect (the fallback): if reload resolves NEITHER a catalog
    // role label NOR any surviving assignment, there is nothing to derive a
    // capability from. Skip `setAuthenticated` entirely rather than hydrate
    // a CurrentUser that can only ever hit NoAccess -- SIGNED_IN stays
    // false, so CD's own AuthGate falls back to its sign-in / persona
    // chooser instead of painting the role-denial screen for a session that
    // simply failed to resolve.
    // Residual split-brain fix (2026-07-28): a value is "resolvable" when it
    // is either a real ROLE_CATALOG label OR a tenant-defined CUSTOM role
    // (access.ts's CUSTOM_ROLES, surfaced via getCustomRoles() -- the SAME
    // acceptance getRolePerms()/effectivePerms() already apply). Gating this
    // check on STUDIO_ROLE_LABELS alone (catalog-only) meant a custom-role
    // assignment that survived BOTH whoami parsers unchanged (mapAssignments
    // passes any dot-free role id through verbatim -- see useWhoami.ts) still
    // got rejected here on reload, even though the fresh in-app sign-in path
    // (access.ts's signIn(), which hydrates CURRENT_USER from whoami with no
    // catalog-only filter at all) accepts that exact same value outright --
    // so a custom-role session derived DIFFERENT authenticated state on
    // reload vs. fresh sign-in. A value unknown to BOTH the catalog AND the
    // tenant's custom-role set is still rejected -- this widens acceptance,
    // it does not remove it.
    const isKnownRoleLabel = (value: string): boolean =>
      STUDIO_ROLE_LABELS.has(value) || Object.prototype.hasOwnProperty.call(getCustomRoles(), value);
    const hasResolvableRole = Boolean(role) && isKnownRoleLabel(role as string);
    const hasResolvableAssignment = assignments
      ? Object.values(assignments).some((value) => isKnownRoleLabel(value))
      : false;
    if (!hasResolvableRole && !hasResolvableAssignment) return;
    const name = displayNameFromSession(session.user.displayName, email);
    const init = session.user.initials.trim() || initialsFromName(name);
    setAuthenticated({
      id: session.user.id || undefined,
      name,
      email,
      init,
      color: "#5142C7",
      role,
      assignments,
      // whoami's `capabilities` is the SAME list the edge gateway enforces on,
      // so it is the authoritative answer to "may this user open this surface".
      // Passing null here discarded it and left `can()` to fall back to
      // `effectivePerms()`, which derives capabilities from the role label
      // alone -- and a non-demo session carries no per-process assignments, so
      // `mapMembershipRoleToStudioRole` collapses every ordinary member to
      // "Event Creator". A user the server had granted schedules.read was
      // therefore shown "Your role can't open this surface" on Workforce while
      // the API would have served them. Preferring the served list makes the UI
      // agree with the enforcement point; the role-derived fallback still
      // applies whenever whoami carries no capabilities.
      //
      // The served list is CANONICAL vocabulary (identity.team.read,
      // appointments.read_any, ...). cd-app's route gates (shell/routeTable.ts
      // ROUTE_READ_PERM) still name the pre-canonical UI vocabulary for the
      // People/Staff surfaces (`team.read`) and for the Appointments surface
      // (`appointments.read`, from APPT_BRIDGE_CAPS), and `can()` checks the
      // snapshot by exact string -- so a System Administrator whose session
      // carried every canonical id was still refused both surfaces. The
      // projection widens the served list with the UI names each canonical id
      // stands for (STUDIO_UI_CAPABILITY_ALIASES, beside
      // CANONICAL_ROLE_ID_TO_STUDIO_ROLE in @v0y4ge/api-contracts settings.ts);
      // the canonical ids stay in place, an empty list stays empty (so the
      // role-derived fallback above is untouched), and nothing outside cd-app's
      // own snapshot ever sees the widened list. It is a shim: it retires when
      // CD re-points the route gates onto canonical ids.
      permissions: session.capabilities ? projectStudioUiCapabilities(session.capabilities) : null,
      locations: null,
    });
  }, [
    whoami.authenticated,
    whoami.loading,
    session.role,
    session.assignments,
    session.capabilities,
    session.user.displayName,
    session.user.email,
    session.user.id,
    session.user.initials,
  ]);

  if (renderLocalSignIn) {
    return (
      <SignInScreen
        tenant={buildLocalSignInBranding()}
        onSubmit={async () => {
          window.location.href = "/dashboard";
        }}
      />
    );
  }

  // Also gate on `demo.loading` (#686): a demo_mode=on tenant must never
  // flash-redirect to SSO (or flash-render nothing) before the runtime probe
  // resolves — one extra paint of loading chrome beats a stale-flash race.
  if (whoami.loading || demo.loading) {
    return <div aria-busy="true" style={{ minHeight: "100vh", background: "var(--color-bg, #f7f7fb)" }} />;
  }

  // JAI-920: hold the same loading chrome while the revoke is in flight. For
  // that window `whoami.authenticated` is still the pre-sign-out answer, so
  // without this the app would fall through to CDApp and its AuthGate -- which
  // already sees SIGNED_IN=false -- would paint the demo persona chooser for a
  // few hundred milliseconds. On a non-demo tenant that chooser is the empty
  // dead-end this ticket is about, so rendering it even briefly reproduces the
  // exact bug being fixed.
  if (signingOut) {
    return <div aria-busy="true" style={{ minHeight: "100vh", background: "var(--color-bg, #f7f7fb)" }} />;
  }

  // A demo_mode tenant's first unauthenticated hit mounts CDApp here too:
  // its AuthGate renders CD's OWN Signin (persona chooser) rather than a
  // studio-shell stand-in — see the `renderDemoSignIn` gate comment above.
  // This branch is reached AFTER whoami/demo have resolved but BEFORE the
  // redirect effect above has actually navigated the browser away (the
  // `window.location.assign(loginUrl)` call is async relative to this
  // render) — an unauthenticated, non-public, non-demo hit sits here for at
  // least one paint while the redirect is in flight. Returning `null` painted
  // a genuinely blank root for that window, which is what tenant#811
  // observed intermittently (~2/10 loads catch this render before the
  // navigation takes effect). Render the same loading placeholder used above
  // instead of `null` so the root never paints blank during that handoff.
  if (!whoami.authenticated && !renderPublicAttendeeRoute && !renderDemoSignIn) {
    return <div aria-busy="true" style={{ minHeight: "100vh", background: "var(--color-bg, #f7f7fb)" }} />;
  }

  return <CDApp />;
}
